import { IconButton, Stack, styled, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { memo, useState } from 'react';
import { StyledParams } from 'types/common';
import { TNote } from 'types/user';
import { AddIcon, EditIcon } from 'assets/icon';
import EditIconMui from '@mui/icons-material/Edit';
import { ButtonCustom } from './ButtonCustom';
type NoteCustomProp = {
  notes: TNote[];
  handleDelete: (idNote: number) => void;
  notesAdmin?: TNote[];
  handleAddNewNote: (textNote: string) => void;
  handleEditNote: (value: { idNote: number; textNote: string; notePhotosIds: number[] }) => void;
  isAdmin: boolean;
};
const NoteCustom = ({
  notes,
  handleDelete,
  notesAdmin,
  isAdmin,
  handleAddNewNote,
  handleEditNote
}: NoteCustomProp) => {
  const [activeNote, setActiveNote] = useState<string>('');
  const [textNote, setTextNote] = useState<string>('');
  const [dateNewNote, setDateNewNote] = useState<string>('');
  const [noteEdit, setNoteEdit] = useState<{
    content: string;
    idNote?: number;
    notePhotosIds: number[];
  }>();

  return (
    <WrapperNoBorder>
      <WrapperHeaderBox>
        <TitleLead>Notes</TitleLead>
        <Stack flexDirection={'row'}>
          {(!isAdmin && notes?.length) || (isAdmin && notesAdmin?.length) ? (
            <IconButton
              onClick={() => {
                setNoteEdit(undefined);
                if (activeNote === 'edit') {
                  setActiveNote('');
                  return;
                }
                setActiveNote('edit');
              }}
              sx={{ width: '46px', height: '46px' }}>
              <EditIcon
                width={'40px'}
                fill={activeNote === 'edit' ? '#22374A' : '#fff'}
                color={activeNote === 'edit' ? '#fff' : '#22374A'}
              />
            </IconButton>
          ) : (
            <> </>
          )}
          <IconButton
            onClick={() => {
              setNoteEdit(undefined);
              if (activeNote === 'add') {
                setActiveNote('');
                return;
              }
              setActiveNote('add');
              setDateNewNote(dayjs().format('M/D/YYYY, h:mm A'));
            }}
            sx={{ width: '46px', height: '46px' }}>
            <AddIcon
              fill={activeNote === 'add' ? '#22374A' : '#fff'}
              color={activeNote === 'add' ? '#fff' : '#22374A'}
            />
          </IconButton>
        </Stack>
      </WrapperHeaderBox>
      <WrapperContentNote>
        <TextNote>Summary Log</TextNote>

        <Stack gap={1.25}>
          {notes?.map((note, index) => {
            if (note.isDelete || note.isAdmin) return;
            return (
              <Stack key={index} gap={0.5} flexDirection={'row'}>
                <TextDate>
                  {(noteEdit?.idNote === note.id ? dayjs() : dayjs(note.createdAt)).format(
                    'M/D/YYYY, h:mm A'
                  )}
                  -
                </TextDate>
                <Stack
                  width={'100%'}
                  flexDirection={'row'}
                  gap={0.5}
                  alignItems={'flex-start'}
                  justifyContent={'space-between'}>
                  {noteEdit?.idNote === note.id ? (
                    <Stack gap={1} alignItems={'flex-end'}>
                      <InputAdd
                        rows={4}
                        value={noteEdit?.content || ''}
                        onChange={(e) =>
                          setNoteEdit({
                            content: e.target.value,
                            idNote: note?.id,
                            notePhotosIds: note?.photos?.map((item) => Number(item.storageId)) || []
                          })
                        }
                      />
                      <ButtonCustom
                        sx={{ width: '96px', height: '32px' }}
                        label={'Edit note'}
                        onClick={() => {
                          if (!noteEdit?.content) {
                            return;
                          }
                          if (!note.id) return;
                          handleEditNote &&
                            handleEditNote({
                              idNote: note.id,
                              textNote: noteEdit.content,
                              notePhotosIds: noteEdit?.notePhotosIds || []
                            });
                          setNoteEdit(undefined);
                        }}
                      />
                    </Stack>
                  ) : (
                    <Stack width={'100%'} gap={0.7}>
                      <TextNote>
                        {note.content} -{' '}
                        <span style={{ color: '#000', fontWeight: 600 }}>
                          {note.user.firstName} {note.user.lastName}
                        </span>
                      </TextNote>
                    </Stack>
                  )}
                  {!isAdmin && activeNote === 'edit' && (
                    <Stack width={'40px'} flexDirection={'row'} gap={'5px'}>
                      <EditIconMui
                        style={{ width: '14px', cursor: 'pointer' }}
                        fontSize={'small'}
                        color={'error'}
                        onClick={() =>
                          setNoteEdit({
                            content: note.content,
                            idNote: note?.id,
                            notePhotosIds: note?.photos?.map((item) => Number(item.storageId)) || []
                          })
                        }
                      />
                      <img
                        onClick={() => {
                          if (note?.id) {
                            const confirmDelete = window.confirm(
                              'Are you sure you want to delete this note?'
                            );
                            if (confirmDelete) {
                              handleDelete && handleDelete(note.id);
                            }
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                        src={'/icons/icon_delete_file.svg'}
                        alt={'icon'}
                      />
                    </Stack>
                  )}
                </Stack>
              </Stack>
            );
          })}
        </Stack>
        <Stack flexDirection={'row'} gap={1.5}>
          {activeNote === 'add' && !isAdmin && (
            <>
              <InputTime disabled value={dateNewNote} onChange={() => {}} />
              <Stack gap={1} width={'100%'} alignItems={'flex-end'}>
                <InputAdd rows={4} value={textNote} onChange={(e) => setTextNote(e.target.value)} />
                <ButtonCustom
                  sx={{ width: '96px', height: '32px' }}
                  label={'Add Note'}
                  onClick={() => {
                    handleAddNewNote && handleAddNewNote(textNote);
                    setActiveNote('');
                    setTextNote('');
                  }}
                />
              </Stack>
            </>
          )}
        </Stack>
        <TextNote>Admin Note:</TextNote>
        <Stack gap={1.25}>
          {notesAdmin?.map((note, index) => {
            if (note.isDelete) return;
            const displayName =
              note?.user?.role === 'ROOT_ADMIN'
                ? 'Master Admin'
                : note?.user?.firstName || note?.user?.lastName
                  ? `${note?.user?.firstName ?? ''} ${note?.user?.lastName ?? ''}`.trim()
                  : 'Admin';
            return (
              <Stack key={index} gap={0.5} flexDirection={'row'}>
                <TextDate>
                  {(noteEdit?.idNote === note.id ? dayjs() : dayjs(note.createdAt)).format(
                    'M/D/YYYY, h:mm A'
                  )}
                  -
                </TextDate>
                <Stack
                  width={'100%'}
                  flexDirection={'row'}
                  gap={0.5}
                  alignItems={'flex-start'}
                  justifyContent={'space-between'}>
                  {noteEdit?.idNote === note.id ? (
                    <Stack gap={1} alignItems={'flex-end'}>
                      <InputAdd
                        rows={4}
                        value={noteEdit?.content || ''}
                        onChange={(e) =>
                          setNoteEdit({
                            content: e.target.value,
                            idNote: note?.id,
                            notePhotosIds: note.photos?.map((item) => Number(item.storageId)) || []
                          })
                        }
                      />
                      <ButtonCustom
                        sx={{ width: '96px', height: '32px' }}
                        label={'Edit note'}
                        onClick={() => {
                          if (!noteEdit?.content) {
                            return;
                          }
                          if (!note.id) return;
                          handleEditNote &&
                            handleEditNote({
                              idNote: note.id,
                              textNote: noteEdit.content,
                              notePhotosIds: noteEdit?.notePhotosIds || []
                            });
                          setNoteEdit(undefined);
                        }}
                      />
                    </Stack>
                  ) : (
                    <Stack width={'100%'} gap={0.7}>
                      <TextNote>
                        {note?.content} -
                        <span style={{ color: '#000', fontWeight: 600 }}>{displayName}</span>
                      </TextNote>
                    </Stack>
                  )}
                  {isAdmin && activeNote === 'edit' && (
                    <Stack width={'40px'} flexDirection={'row'} gap={'5px'}>
                      <EditIconMui
                        style={{ width: '14px', cursor: 'pointer' }}
                        fontSize={'small'}
                        color={'error'}
                        onClick={() =>
                          setNoteEdit({
                            content: note.content,
                            idNote: note?.id,
                            notePhotosIds: note.photos?.map((item) => Number(item.storageId)) || []
                          })
                        }
                      />
                      <img
                        onClick={() => {
                          if (note?.id) {
                            const confirmDelete = window.confirm(
                              'Are you sure you want to delete this note?'
                            );
                            if (!confirmDelete) return;
                            handleDelete && handleDelete(note.id);
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                        src={'/icons/icon_delete_file.svg'}
                        alt={'icon'}
                      />
                    </Stack>
                  )}
                </Stack>
              </Stack>
            );
          })}
        </Stack>
        <Stack flexDirection={'row'} gap={1.5}>
          {activeNote === 'add' && isAdmin && (
            <>
              <InputTime disabled value={dateNewNote} onChange={() => {}} />
              <Stack gap={1} width={'100%'} alignItems={'flex-end'}>
                <InputAdd rows={4} value={textNote} onChange={(e) => setTextNote(e.target.value)} />
                <ButtonCustom
                  sx={{ width: '96px', height: '32px' }}
                  label={'Add Note'}
                  onClick={() => {
                    handleAddNewNote && handleAddNewNote(textNote);
                    setActiveNote('');
                    setTextNote('');
                  }}
                />
              </Stack>
            </>
          )}
        </Stack>
      </WrapperContentNote>
    </WrapperNoBorder>
  );
};

export const NoteCustomMemo = memo(NoteCustom);
const InputTime = styled('input')(() => ({
  border: 'none',
  height: '30px',
  width: '140px',
  fontSize: '14px',
  borderRadius: '5px',
  paddingLeft: '5px',
  color: '#ADADAD',
  '&:disabled': {
    background: '#fff'
  }
}));
const InputAdd = styled('textarea')(() => ({
  border: 'none',
  minHeight: '30px',
  fontSize: '14px',
  borderRadius: '5px',
  padding: '6px 10px',
  width: '100%',
  color: '#565656'
}));
const WrapperContentNote = styled(Stack)(({ theme }: StyledParams) => ({
  background: theme.colors.blue,
  borderRadius: '5px',
  width: '100%',
  padding: '8px 10px',
  gap: '10px'
}));
const TextNote = styled(Typography)(() => ({
  color: '#565656',
  fontSize: 14,
  fontWeight: 500,
  lineHeight: '100%',
  whiteSpace: 'pre-line'
}));

const TextDate = styled(Typography)(() => ({
  color: '#ADADAD',
  fontSize: 14,
  fontWeight: 500,
  lineHeight: '100%',
  minWidth: '140px'
}));
const WrapperNoBorder = styled(Stack)(() => ({
  paddingBottom: '16px'
}));
const WrapperHeaderBox = styled(Stack)(() => ({
  width: '100%',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingBottom: '10px'
}));
const TitleLead = styled(Typography)(({ theme }: StyledParams) => ({
  color: theme.colors.light1,
  fontSize: 18,
  fontWeight: 500,
  lineHeight: '100%'
}));
